import { useState, useEffect } from 'react'
import { X, Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { 
  getLoyaltyProgramsCatalog, 
  saveLoyaltyMembership
} from '@/lib/profile'
import type { LoyaltyProgramCatalog, UserLoyaltyMembership } from '@/types/profile'

interface LoyaltyProgramFormProps {
  userId: string
  membership?: UserLoyaltyMembership | null
  onSave: () => void
  onCancel: () => void
}

const TYPE_LABELS: Record<string, string> = {
  airline: 'Airline',
  hotel: 'Hotel',
  car_rental: 'Car Rental',
  credit_card: 'Credit Card',
  other: 'Other'
}

export function LoyaltyProgramForm({ 
  userId, 
  membership, 
  onSave, 
  onCancel 
}: LoyaltyProgramFormProps) {
  const [programs, setPrograms] = useState<LoyaltyProgramCatalog[]>([])
  const [selectedType, setSelectedType] = useState<string>(
    membership?.program?.program_type || 'airline'
  )
  const [selectedProgram, setSelectedProgram] = useState<string>(
    membership?.program_id || ''
  )
  const [formData, setFormData] = useState({
    member_id: membership?.member_id || '',
    tier: membership?.tier || '',
    points_balance: membership?.points_balance || 0,
    is_primary: membership?.is_primary || false
  })
  const [isSaving, setIsSaving] = useState(false)
  const [showTypeDropdown, setShowTypeDropdown] = useState(false)
  const [showProgramDropdown, setShowProgramDropdown] = useState(false)
  
  useEffect(() => {
    loadPrograms()
  }, [selectedType])
  
  async function loadPrograms() {
    try {
      const data = await getLoyaltyProgramsCatalog(selectedType as any)
      setPrograms(data)
      
      // Reset program selection if type changes
      if (!membership) {
        setSelectedProgram('')
      }
    } catch (error) {
      console.error('Failed to load loyalty programs:', error)
    }
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedProgram) return
    
    setIsSaving(true)
    try {
      if (membership?.id) {
        // Update existing membership
        await saveLoyaltyMembership({
          ...membership,
          member_id: formData.member_id || undefined,
          tier: formData.tier || undefined,
          points_balance: formData.points_balance || undefined,
          is_primary: formData.is_primary
        })
      } else {
        // Create new membership
        await saveLoyaltyMembership({
          user_id: userId,
          program_id: selectedProgram,
          member_id: formData.member_id || undefined,
          tier: formData.tier || undefined,
          points_balance: formData.points_balance || undefined,
          is_primary: formData.is_primary,
          is_active: true
        })
      }
      
      onSave()
    } catch (error) {
      console.error('Failed to save loyalty membership:', error)
    } finally {
      setIsSaving(false)
    }
  }
  
  const selectedProgramData = programs.find(p => p.id === selectedProgram)
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl max-w-lg w-full p-8 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {membership ? 'Edit' : 'Add'} Loyalty Program
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Program Type Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Program Type
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                className={cn(
                  "w-full px-4 py-3 rounded-2xl border-2 border-[#E8E8FF]",
                  "bg-[#E8E8FF]/30 text-left font-medium text-gray-700",
                  "hover:bg-[#E8E8FF]/50 transition-colors",
                  "flex items-center justify-between"
                )}
              >
                <span>{TYPE_LABELS[selectedType]}</span>
                <ChevronDown className="h-4 w-4 text-gray-500" />
              </button>
              
              {showTypeDropdown && (
                <div className="absolute top-full mt-2 w-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden z-10">
                  {Object.entries(TYPE_LABELS).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setSelectedType(value)
                        setShowTypeDropdown(false)
                      }}
                      className={cn(
                        "w-full px-4 py-3 text-left hover:bg-[#E8E8FF]/50 transition-colors",
                        selectedType === value && "bg-[#E8E8FF] text-[#5B6FED]"
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Program Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Select Program
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowProgramDropdown(!showProgramDropdown)}
                className={cn(
                  "w-full px-4 py-3 rounded-2xl border-2",
                  selectedProgram 
                    ? "border-[#5B6FED] bg-[#E8E8FF] text-[#5B6FED]" 
                    : "border-[#E8E8FF] bg-[#E8E8FF]/30 text-gray-500",
                  "text-left font-medium",
                  "hover:bg-[#E8E8FF] transition-colors",
                  "flex items-center justify-between"
                )}
              >
                <span>
                  {selectedProgramData?.program_name || 'Choose a program'}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-500" />
              </button>
              
              {showProgramDropdown && (
                <div className="absolute top-full mt-2 w-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden z-10 max-h-64 overflow-y-auto">
                  {programs.map(program => (
                    <button
                      key={program.id}
                      type="button"
                      onClick={() => {
                        setSelectedProgram(program.id)
                        setShowProgramDropdown(false)
                      }}
                      className={cn(
                        "w-full px-4 py-3 text-left hover:bg-[#E8E8FF]/50 transition-colors",
                        selectedProgram === program.id && "bg-[#E8E8FF] text-[#5B6FED]"
                      )}
                    >
                      <div>
                        <div className="font-medium">{program.program_name}</div>
                        {program.company && (
                          <div className="text-xs text-gray-500">{program.company}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Member ID Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Member ID / Number
            </label>
            <input
              type="text"
              value={formData.member_id}
              onChange={(e) => setFormData({...formData, member_id: e.target.value})}
              className={cn(
                "w-full px-4 py-3 rounded-2xl border-2 border-[#E8E8FF]",
                "bg-white placeholder-gray-400",
                "focus:border-[#5B6FED] focus:outline-none transition-colors"
              )}
              placeholder="Enter your member ID"
            />
          </div>
          
          {/* Status/Tier Selector */}
          {selectedProgramData && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Status / Tier
              </label>
              {selectedProgramData.tier_names && selectedProgramData.tier_names.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedProgramData.tier_names.map(tier => (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => setFormData({...formData, tier})}
                      className={cn(
                        "px-4 py-2 rounded-full text-sm font-medium transition-all",
                        formData.tier === tier
                          ? "bg-[#5B6FED] text-white"
                          : "bg-[#E8E8FF] text-[#5B6FED] hover:bg-[#D9D9FF]"
                      )}
                    >
                      {tier}
                    </button>
                  ))}
                </div>
              ) : (
                <input
                  type="text"
                  value={formData.tier}
                  onChange={(e) => setFormData({...formData, tier: e.target.value})}
                  className={cn(
                    "w-full px-4 py-3 rounded-2xl border-2 border-[#E8E8FF]",
                    "bg-white placeholder-gray-400",
                    "focus:border-[#5B6FED] focus:outline-none transition-colors"
                  )}
                  placeholder="Enter your status tier"
                />
              )}
            </div>
          )}
          
          {/* Points Balance */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Points Balance (optional)
            </label>
            <input
              type="number"
              value={formData.points_balance || ''}
              onChange={(e) => setFormData({
                ...formData, 
                points_balance: parseInt(e.target.value) || 0
              })}
              className={cn(
                "w-full px-4 py-3 rounded-2xl border-2 border-[#E8E8FF]",
                "bg-white placeholder-gray-400",
                "focus:border-[#5B6FED] focus:outline-none transition-colors"
              )}
              placeholder="0"
            />
          </div>
          
          {/* Primary Program Toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_primary}
              onChange={(e) => setFormData({...formData, is_primary: e.target.checked})}
              className="sr-only"
            />
            <div 
              className={cn(
                "w-12 h-6 rounded-full relative transition-colors",
                formData.is_primary ? "bg-[#5B6FED]" : "bg-gray-200"
              )}
            >
              <div 
                className={cn(
                  "absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full",
                  "transition-transform",
                  formData.is_primary && "translate-x-6"
                )}
              />
            </div>
            <span className="text-sm font-medium text-gray-700">
              Set as primary program for this type
            </span>
          </label>
          
          {/* Program Benefits */}
          {selectedProgramData?.benefits_summary && (
            <div className="bg-[#E8E8FF]/30 rounded-2xl p-4">
              <p className="text-sm font-medium text-[#5B6FED] mb-1">
                Program Benefits
              </p>
              <p className="text-sm text-gray-600">
                {selectedProgramData.benefits_summary}
              </p>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className={cn(
                "flex-1 px-6 py-3 rounded-full",
                "border-2 border-[#E8E8FF] text-gray-600 font-medium",
                "hover:bg-gray-50 transition-colors"
              )}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedProgram || isSaving}
              className={cn(
                "flex-1 px-6 py-3 rounded-full",
                "bg-[#5B6FED] text-white font-medium",
                "hover:bg-[#4A5FDD] transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "flex items-center justify-center gap-2"
              )}
            >
              <Check className="h-4 w-4" />
              {isSaving ? 'Saving...' : (membership ? 'Update' : 'Add')} Program
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}